import { createFileRoute } from '@tanstack/react-router'
import { useRef, useCallback } from 'react'
import PageHeader from '@/components/settings/PageHeader'
import { useUserSubscribersInfinite } from '@/hooks/api/useUsers'
import { EmptyState } from '@/components/shared/EmptyState'
import type { UserSummaryDto } from '@wtf/shared'
import { UserCard } from '@/components/shared/UserCard'
import { assetPath } from '@/utils/assetPath'

export const Route = createFileRoute('/profiles_/$id/subscribers')({
    component: SubscribedRoute,
})

function SubscribedRoute() {
    const { id } = Route.useParams()
    const {
        data,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useUserSubscribersInfinite(id, 10)

    // Intersection observer for infinite scrolling
    const observer = useRef<IntersectionObserver | null>(null)
    const lastElementRef = useCallback(
        (node: HTMLDivElement) => {
            if (isLoading) return
            if (observer.current) observer.current.disconnect()
            observer.current = new IntersectionObserver((entries) => {
                if (
                    entries[0].isIntersecting &&
                    hasNextPage &&
                    !isFetchingNextPage
                ) {
                    fetchNextPage()
                }
            })
            if (node) observer.current.observe(node)
        },
        [isLoading, hasNextPage, isFetchingNextPage, fetchNextPage]
    )

    // Flatten all pages data
    const allSubscriptions =
        data?.pages.flatMap((page) => page.subscribers) ?? []
    const totalCount = data?.pages[0]?.pagination.total ?? 0

    // Loading state
    if (isLoading) {
        return (
            <div className="h-dvh overflow-y-auto px-2 pb-14">
                <PageHeader title="Followers" />
                <div className="mb-20 p-2">
                    {Array.from({ length: 5 }).map((_, idx) => (
                        <UserCard loading key={idx} className="mb-2" />
                    ))}
                </div>
            </div>
        )
    }

    // Error state
    if (isError) {
        return (
            <div className="h-dvh overflow-y-auto px-2 pb-14">
                <PageHeader
                    title="Followers"
                    className="bg-[var(--color-bg)]"
                />
                <EmptyState
                    icon={assetPath("icons/status/error.svg")}
                    title="Something went wrong"
                    description="We couldn't load the followers list. Please try again."
                    actionText="Retry"
                    onAction={() => window.location.reload()}
                />
            </div>
        )
    }

    return (
        <div className="h-dvh overflow-y-auto px-2 pb-14">
            <PageHeader title={`Followers (${totalCount})`} />
            <div className="mb-20 space-y-2 p-2">
                {allSubscriptions.length === 0 ? (
                    <EmptyState
                        icon={assetPath("icons/status/empty.svg")}
                        title="No followers yet"
                        description="When other users follow you, they'll appear here."
                    />
                ) : (
                    <>
                        {allSubscriptions.map(
                            (user: UserSummaryDto, index: number) => (
                                <div
                                    key={user.id}
                                    ref={
                                        index === allSubscriptions.length - 1
                                            ? lastElementRef
                                            : undefined
                                    }
                                >
                                    <UserCard user={user} />
                                </div>
                            )
                        )}

                        {/* Loading indicator for next page */}
                        {isFetchingNextPage && (
                            <div className="py-4">
                                {Array.from({ length: 3 }).map((_, idx) => (
                                    <UserCard
                                        loading
                                        key={idx}
                                        className="mb-2"
                                    />
                                ))}
                            </div>
                        )}

                        {/* End of list indicator */}
                        {!hasNextPage && allSubscriptions.length > 0 && (
                            <div className="py-4 text-center">
                                <p className="text-sm text-gray-500">
                                    You've reached the end of the list
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
